import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Search, X, Car, Loader2, Wrench, Plus, ShieldCheck, Camera, Upload, Image as ImageIcon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import VehiculePicker from "@/components/VehiculePicker";
import ClientSelectedCard from "@/components/ClientSelectedCard";
import QuickClientDialog from "@/components/QuickClientDialog";
import QuickVehiculeDialog from "@/components/QuickVehiculeDialog";
import { useAuth } from "@/contexts/AuthContext";
import OrPhotoLibraryDialog from "@/components/or/OrPhotoLibraryDialog";
import CarteGriseOrDialog from "@/components/photos/CarteGriseOrDialog";
import CarteGriseAdjustDialog from "@/components/photos/CarteGriseAdjustDialog";
import { photoDataUrl } from "@/components/photos/photoDataUrl";
import { photoCompress } from "@/components/or/photoCompress";

export default function OrCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const prefilled = location.state || {};

  const [client, setClient] = useState(prefilled.client || null);
  const [vehicules, setVehicules] = useState([]);
  const [vehiculeId, setVehiculeId] = useState(prefilled.vehiculeId || "");
  const [rdvId] = useState(prefilled.rdvId || null);
  const [heuresEstimees, setHeuresEstimees] = useState(prefilled.heuresEstimees ?? "");
  const [typeClientTarif, setTypeClientTarif] = useState("particulier");
  // iter233 — Dossier assurance renseigné dès la création de l'OR
  const [assurance, setAssurance] = useState(null);
  const [assuranceQuery, setAssuranceQuery] = useState("");
  const [assuranceResults, setAssuranceResults] = useState([]);
  const [noSinistre, setNoSinistre] = useState("");
  const [kmDepose, setKmDepose] = useState("");
  const [notesClient, setNotesClient] = useState(prefilled.notesClient || "");
  const [notesInternes, setNotesInternes] = useState("");
  // iter293 — publication sur le lien de suivi : fermé par défaut
  const [suiviPublie, setSuiviPublie] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickClientOpen, setQuickClientOpen] = useState(false);
  const [quickVehiculeOpen, setQuickVehiculeOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // iter266 — Attribution : un mécano s'attribue l'OR automatiquement côté serveur ;
  // admin / comptoir désignent l'intervenant ici.
  const [intervenantId, setIntervenantId] = useState("");
  const [mecanos, setMecanos] = useState([]);
  const choisitIntervenant = ["admin", "comptoir"].includes(user?.role);
  // iter280 — Création express depuis une carte grise (photo CRM, fichier ou appareil)
  const [cgLibOpen, setCgLibOpen] = useState(false);
  const [cgBusy, setCgBusy] = useState(false);
  const [cgAnalyse, setCgAnalyse] = useState(null);
  const [cgPhotoId, setCgPhotoId] = useState(null);
  const cgGalerieRef = useRef(null);
  const cgCamRef = useRef(null);
  // iter282 — cadrage / rotation avant lecture
  const [cgAjustSrc, setCgAjustSrc] = useState(null);
  const [cgNouvellePhoto, setCgNouvellePhoto] = useState(null);

  // Photo déjà dans la bibliothèque : on la récupère pour la cadrer
  const preparerPhotoCrm = async (pid) => {
    setCgLibOpen(false);
    setCgBusy(true);
    try {
      setCgPhotoId(pid);
      setCgNouvellePhoto(null);
      setCgAjustSrc(await photoDataUrl(pid));
    } catch {
      toast.error("Photo illisible");
    } finally {
      setCgBusy(false);
    }
  };

  // Fichier ou prise de vue : on cadre d'abord, on enregistre la version redressée
  const lireFichierCarteGrise = async (file) => {
    if (!file) return;
    setCgBusy(true);
    try {
      setCgPhotoId(null);
      setCgNouvellePhoto(true);
      setCgAjustSrc(await photoCompress(file, 900));
    } catch {
      toast.error("Photo illisible");
    } finally {
      setCgBusy(false);
    }
  };

  const analyserCarteGrise = async (image) => {
    setCgBusy(true);
    try {
      let pid = cgPhotoId;
      let body = { image };
      if (cgNouvellePhoto) {
        const { data } = await api.post("/photos", { photos: [image] });
        pid = data.items?.[0]?.id;
        body = {};
        setCgPhotoId(pid);
      }
      const { data } = await api.post(`/photos/${pid}/carte-grise/analyser`, body);
      setCgAjustSrc(null);
      setCgAnalyse(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lecture de la carte grise impossible");
    } finally {
      setCgBusy(false);
    }
  };

  useEffect(() => {
    if (!choisitIntervenant) return;
    api.get("/atelier/mecaniciens")
      .then(({ data }) => setMecanos(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setMecanos([]));
  }, [choisitIntervenant]);

  // Live search
  useEffect(() => {
    if (!query || client) { setResults([]); setSearched(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query } });
        setResults(data.clients || []);
        setSearched(true);
      } catch (e) { setResults([]); setSearched(true); }
    }, 200);
    return () => clearTimeout(t);
  }, [query, client]);

  // Load vehicules when client selected
  useEffect(() => {
    if (!client?.id) { setVehicules([]); return; }
    api.get(`/clients/${client.id}`).then((r) => {
      setVehicules(r.data.vehicules || []);
      // Auto select first vehicule
      const principal = (r.data.vehicules || []).find((v) => v.est_principal);
      if (principal && !vehiculeId) setVehiculeId(principal.id);
    }).catch(() => {});
    // Auto-detect tarif based on client type
    if (client.type === "entreprise") setTypeClientTarif("flotte");
  }, [client?.id]);

  // iter346 — Le kilométrage de dépose n'est plus pré-rempli : le dernier km
  // connu du véhicule (parfois ancien ou issu d'une lecture de photo) s'affichait
  // comme s'il avait été relevé. Il est désormais montré en simple information
  // sous le champ, qui reste vide tant que personne ne saisit la valeur réelle.
  const kmDernierVehicule = (() => {
    const v = vehicules.find((x) => x.id === vehiculeId);
    const km = v?.km_dernier ?? v?.kilometrage;
    return km === null || km === undefined || km === "" ? null : km;
  })();

  // iter233 — Recherche d'assurance (dossier sinistre) à la création
  useEffect(() => {
    if (typeClientTarif !== "assurance" || assurance) { setAssuranceResults([]); return; }
    const q = assuranceQuery.trim();
    if (q.length < 2) { setAssuranceResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { q, type: "assurance", limit: 15 } });
        setAssuranceResults(data?.items || data || []);
      } catch { setAssuranceResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [assuranceQuery, typeClientTarif, assurance]);

  // iter348 — Véhicule d'un autre client (gestionnaire de flotte) : on demande,
  // avant de créer l'OR, s'il doit apparaître sur les liens de suivi du client
  // du dossier. Un « non » masque le véhicule sur ce lien-là uniquement.
  const [liensDialog, setLiensDialog] = useState(null);   // {vehicule, proprietaire, liens, choix}

  const creerOr = async (masquerSurLiens = []) => {
    setSubmitting(true);
    try {
      const payload = {
        client_id: client.id,
        vehicule_id: vehiculeId || null,
        rdv_id: rdvId || null,
        heures_estimees: heuresEstimees !== "" ? parseFloat(heuresEstimees) : null,
        type_client_tarif: typeClientTarif,
        km_depose: kmDepose ? parseInt(kmDepose, 10) : null,
        notes_client: notesClient || null,
        notes_internes: notesInternes || null,
        photos_depose: [],
        // iter293 — rien n'est visible ni notifié sur le lien de suivi sans ce feu vert
        suivi_publie: suiviPublie,
      };
      if (intervenantId) payload.intervenant_principal_id = intervenantId;
      // iter233 — Assurance + N° de sinistre rattachés dès l'ouverture du dossier
      if (typeClientTarif === "assurance") {
        if (assurance?.id) payload.assurance_id = assurance.id;
        payload.references = [
          { key: "no_sinistre", label: "N° de sinistre", value: noSinistre.trim() },
        ];
      }
      const { data } = await api.post("/or", payload);
      for (const lienId of masquerSurLiens) {
        try {
          await api.post(`/suivi-tokens/${lienId}/masquer-vehicule`, { vehicule_id: vehiculeId, masquer: true });
        } catch {
          toast.warning("OR créé, mais le masquage sur un lien de suivi a échoué");
        }
      }
      toast.success(`OR ${data.numero} créé`);
      navigate(`/atelier/or/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!client?.id) {
      toast.error("Sélectionnez un client");
      return;
    }
    if (!vehiculeId) {
      toast.error("Un véhicule est obligatoire pour créer un OR. Sélectionnez-en un ou créez-en un nouveau.");
      return;
    }
    try {
      const { data } = await api.get("/suivi-tokens/liens-concernes", {
        params: { client_id: client.id, vehicule_id: vehiculeId },
      });
      if (data.concerne && (data.liens || []).length) {
        setLiensDialog({
          ...data,
          choix: Object.fromEntries(data.liens.map((l) => [l.id, !l.deja_masque])),
        });
        return;
      }
    } catch {
      // Le contrôle des liens ne doit jamais empêcher d'ouvrir un dossier
    }
    await creerOr();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="or-create-page">
      <button onClick={() => navigate("/atelier")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-2">
        <Wrench className="w-7 h-7 text-[#52a675]" /> Nouvel ordre de réparation
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Vous pouvez ajouter le devis (lignes) et les photos après la création.
      </p>

      <Card className="p-5 border-gray-200 space-y-4">
        <div>
          <Label>Client *</Label>
          {client ? (
            <ClientSelectedCard
              client={client}
              onClear={() => { setClient(null); setVehiculeId(""); setVehicules([]); }}
              testIdPrefix="or-client"
            />
          ) : (
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={query} onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }} onFocus={() => setSearchOpen(true)} placeholder="Rechercher un client (nom, plaque, tél)…" className="pl-10" data-testid="or-client-search" />
              </div>
              {searchOpen && query && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
                  {results.map((c) => {
                    const nom = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          setClient({ ...c, nom });
                          setQuery(""); setSearchOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#f0f9f4] text-sm border-b border-gray-50 last:border-0"
                        data-testid={`or-pick-client-${c.id}`}
                      >
                        <div className="font-semibold">{nom}</div>
                        <div className="text-xs text-gray-500">{c.tel_principal} · {c.localite}</div>
                      </button>
                    );
                  })}
                  {searched && results.length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-500 border-b border-gray-50">
                      Aucun client trouvé pour « <span className="font-semibold">{query}</span> »
                    </div>
                  )}
                  <button
                    onClick={() => { setQuickClientOpen(true); setSearchOpen(false); }}
                    className="w-full text-left px-3 py-2.5 text-sm bg-[#f0f9f4] hover:bg-[#52a675]/15 text-[#3d8a5c] font-semibold flex items-center gap-2"
                    data-testid="or-quick-create-client"
                  >
                    <Plus className="w-4 h-4" />
                    Créer un nouveau client {query && `« ${query} »`}
                  </button>
                </div>
              )}
              {!query && (
                <button
                  onClick={() => setQuickClientOpen(true)}
                  className="mt-2 text-xs text-[#3d8a5c] hover:underline inline-flex items-center gap-1"
                  data-testid="or-quick-create-client-empty"
                >
                  <Plus className="w-3.5 h-3.5" /> ou créer un nouveau client
                </button>
              )}
            </div>
          )}
        </div>

        {client && (
          <div data-testid="or-vehicule-section">
            <Label>Véhicule *</Label>
            {vehicules.length > 0 ? (
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                <VehiculePicker
                  vehicules={vehicules}
                  value={vehiculeId}
                  onChange={setVehiculeId}
                  testId="or-vehicule"
                  showDetails
                  onVehiculeUpdated={(v) => setVehicules((prev) => prev.map((x) => (x.id === v.id ? { ...x, ...v } : x)))}
                />
                <Button variant="outline" onClick={() => setQuickVehiculeOpen(true)} data-testid="or-add-vehicule">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 p-3 border border-dashed border-orange-300 bg-orange-50 rounded-md">
                <div className="text-sm text-orange-900">
                  Ce client n'a aucun véhicule enregistré. Ajoutez-en un pour créer l'OR.
                </div>
                <Button
                  size="sm"
                  onClick={() => setQuickVehiculeOpen(true)}
                  className="bg-[#52a675] hover:bg-[#3d8a5c] flex-shrink-0"
                  data-testid="or-add-vehicule-empty"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ajouter un véhicule
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Type de tarif</Label>
            <Select value={typeClientTarif} onValueChange={setTypeClientTarif}>
              <SelectTrigger data-testid="or-type-tarif"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="assurance">Assurance</SelectItem>
                <SelectItem value="flotte">Flotte</SelectItem>
                <SelectItem value="interne">Interne</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Km à la dépose</Label>
            <Input type="number" value={kmDepose} onChange={(e) => setKmDepose(e.target.value)} placeholder="À relever sur le compteur" data-testid="or-km" />
            {kmDernierVehicule != null && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="or-km-dernier-hint">
                Dernier km enregistré : {Number(kmDernierVehicule).toLocaleString("fr-CH").replace(/\u202f|\u00a0|,/g, "'")} km
                <button
                  type="button"
                  onClick={() => setKmDepose(String(kmDernierVehicule))}
                  className="ml-2 underline hover:text-gray-700"
                  data-testid="or-km-reprendre-btn"
                >
                  reprendre
                </button>
              </p>
            )}
          </div>
          {choisitIntervenant && (
            <div>
              <Label>Attribué à</Label>
              <Select value={intervenantId || "aucun"} onValueChange={(v) => setIntervenantId(v === "aucun" ? "" : v)}>
                <SelectTrigger data-testid="or-intervenant-select">
                  <SelectValue placeholder="Personne pour l'instant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucun" data-testid="or-intervenant-option-aucun">Personne pour l&apos;instant</SelectItem>
                  {mecanos.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`or-intervenant-option-${m.id}`}>
                      {m.label || m.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-gray-400 mt-0.5">Le mécano retrouvera l&apos;OR dans sa vue « Mes OR ».</p>
            </div>
          )}
          <div>
            <Label>Temps estimé (h)</Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              max="16"
              value={heuresEstimees}
              onChange={(e) => setHeuresEstimees(e.target.value)}
              placeholder="Ex: 2"
              data-testid="or-heures-estimees"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Pour la capacité atelier journalière. Modifiable plus tard.</p>
          </div>
        </div>

        {/* iter233 — Dossier assurance : compagnie + N° de sinistre dès l'ouverture */}
        {typeClientTarif === "assurance" && (
          <div
            className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-3"
            data-testid="or-create-assurance-block"
          >
            <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Dossier assurance
            </div>
            <div>
              <Label className="text-xs">Assurance concernée</Label>
              {assurance ? (
                <div className="flex items-center gap-2 p-2 border border-violet-200 rounded-md bg-white">
                  <ShieldCheck className="w-4 h-4 text-violet-600" />
                  <div className="flex-1 text-sm font-semibold truncate">{assurance.raison_sociale}</div>
                  <button
                    onClick={() => { setAssurance(null); setAssuranceQuery(""); }}
                    className="text-gray-400 hover:text-red-600"
                    data-testid="or-create-assurance-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={assuranceQuery}
                    onChange={(e) => setAssuranceQuery(e.target.value)}
                    placeholder="Rechercher une assurance (AXA, Generali, Zurich…)"
                    className="pl-10 bg-white"
                    data-testid="or-create-assurance-search"
                  />
                  {assuranceResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                      {assuranceResults.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => { setAssurance(a); setAssuranceResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm border-b border-gray-50 last:border-0"
                          data-testid={`or-create-assurance-option-${a.id}`}
                        >
                          <div className="font-semibold">{a.raison_sociale}</div>
                          <div className="text-xs text-gray-500">
                            {[a.localite, a.email].filter(Boolean).join(" · ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">N° de sinistre</Label>
              <Input
                value={noSinistre}
                onChange={(e) => setNoSinistre(e.target.value)}
                placeholder="Ex: CH121608/01"
                className="bg-white font-mono"
                data-testid="or-create-no-sinistre"
              />
              <p className="text-[10px] text-violet-700 mt-0.5">
                Repris automatiquement sur le devis, la facture, l&apos;objet des e-mails et la recherche.
              </p>
            </div>
          </div>
        )}

        <div>
          <Label>Notes du client (motif d&apos;intervention)</Label>
          <Textarea value={notesClient} onChange={(e) => setNotesClient(e.target.value)} rows={2} placeholder="Bruit côté droit, voyant moteur allumé…" data-testid="or-notes-client" />
        </div>

        <div>
          <Label>Notes internes (atelier)</Label>
          <Textarea value={notesInternes} onChange={(e) => setNotesInternes(e.target.value)} rows={2} data-testid="or-notes-internes" />
        </div>

        {/* iter293 — publication sur le lien de suivi, décidée à l'ouverture du dossier */}
        <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 cursor-pointer">
          <input
            type="checkbox"
            checked={suiviPublie}
            onChange={(e) => setSuiviPublie(e.target.checked)}
            className="accent-[#52a675] w-4 h-4 mt-0.5"
            data-testid="or-suivi-publie"
          />
          <span className="text-sm text-gray-800">
            Publier ce dossier sur le lien de suivi du client
            <span className="block text-[11px] text-gray-500">
              Décoché, le dossier reste invisible du client et aucune notification ne part.
              Vous pourrez le publier plus tard depuis la fiche de l&apos;OR.
            </span>
          </span>
        </label>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-3 border-t border-gray-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={cgBusy} className="border-[#52a675]/50 text-[#2d5f3f]" data-testid="or-create-cg-btn">
                {cgBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2 text-[#52a675]" />}
                Créer depuis une carte grise
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-gray-400">
                Lecture automatique (CH, FR, DE, IT, AT)
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setCgLibOpen(true)} data-testid="or-create-cg-crm">
                <ImageIcon className="w-4 h-4 mr-2 text-[#52a675]" /> Photos du CRM
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => cgGalerieRef.current?.click()} data-testid="or-create-cg-galerie">
                <Upload className="w-4 h-4 mr-2 text-gray-500" /> Choisir un fichier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => cgCamRef.current?.click()} data-testid="or-create-cg-camera">
                <Camera className="w-4 h-4 mr-2 text-gray-500" /> Prendre une photo
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            ref={cgGalerieRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => lireFichierCarteGrise(e.target.files?.[0])}
            data-testid="or-create-cg-galerie-input"
          />
          <input
            ref={cgCamRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => lireFichierCarteGrise(e.target.files?.[0])}
            data-testid="or-create-cg-camera-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate("/atelier")}>Annuler</Button>
            <Button onClick={submit} disabled={submitting || !client || !vehiculeId} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="or-create-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer l'OR"}
            </Button>
          </div>
        </div>
      </Card>

      <QuickClientDialog
        open={quickClientOpen}
        onClose={() => setQuickClientOpen(false)}
        initialQuery={query}
        onCreated={(c) => {
          setClient(c);
          setQuery("");
          setResults([]);
        }}
      />

      <QuickVehiculeDialog
        open={quickVehiculeOpen}
        onClose={() => setQuickVehiculeOpen(false)}
        clientId={client?.id}
        clientNom={client?.nom}
        onCreated={(v) => {
          setVehicules((prev) => [...prev, v]);
          setVehiculeId(v.id);
        }}
      />

      {/* iter280 — raccourci carte grise : lecture Claude puis OR créé directement */}
      <OrPhotoLibraryDialog
        open={cgLibOpen}
        onClose={() => setCgLibOpen(false)}
        titre="Choisir la photo de la carte grise"
        onPick={(ids) => preparerPhotoCrm(ids[0])}
      />
      <CarteGriseAdjustDialog
        open={!!cgAjustSrc}
        src={cgAjustSrc}
        busy={cgBusy}
        onCancel={() => setCgAjustSrc(null)}
        onValidate={analyserCarteGrise}
      />
      <CarteGriseOrDialog
        open={!!cgAnalyse}
        photoId={cgPhotoId}
        analyse={cgAnalyse}
        onClose={() => setCgAnalyse(null)}
      />

      {/* iter348 — Publication d'un véhicule d'un autre client sur les liens de suivi */}
      <Dialog open={!!liensDialog} onOpenChange={(v) => !v && !submitting && setLiensDialog(null)}>
        <DialogContent data-testid="or-liens-suivi-dialog">
          <DialogHeader>
            <DialogTitle>Publier ce véhicule sur le lien de suivi ?</DialogTitle>
            <DialogDescription>
              Le véhicule <b>{liensDialog?.vehicule?.label}</b> appartient à{" "}
              <b>{liensDialog?.proprietaire}</b>, mais le dossier est ouvert au nom de{" "}
              <b>{client?.raison_sociale || `${client?.prenom || ""} ${client?.nom || ""}`.trim()}</b>.
              Choisissez s&apos;il doit apparaître sur ses liens de suivi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(liensDialog?.liens || []).map((l) => (
              <label
                key={l.id}
                className="flex items-start gap-2 text-sm border border-gray-200 rounded px-3 py-2 cursor-pointer"
                data-testid={`or-lien-suivi-${l.id}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!liensDialog.choix[l.id]}
                  onChange={(e) => setLiensDialog((d) => ({
                    ...d, choix: { ...d.choix, [l.id]: e.target.checked },
                  }))}
                  data-testid={`or-lien-suivi-check-${l.id}`}
                />
                <span>
                  <span className="font-medium">{l.label || "Lien de suivi"}</span>
                  <span className="block text-xs text-gray-500">{(l.clients || []).join(", ")}</span>
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLiensDialog(null)} disabled={submitting} data-testid="or-liens-suivi-cancel">
              Annuler
            </Button>
            <Button
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              disabled={submitting}
              onClick={() => {
                const masquer = (liensDialog.liens || [])
                  .filter((l) => !liensDialog.choix[l.id])
                  .map((l) => l.id);
                setLiensDialog(null);
                creerOr(masquer);
              }}
              data-testid="or-liens-suivi-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Créer l&apos;OR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
